

import React, { useEffect, useState, useMemo } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import GenericDraggableTableView from "../../Features/Table/GenericDraggableTable";
import { getColumnsConfig } from "../../Features/Table/GenericDraggableTable/GenericDraggableTableFuncations";
import { TABLE_COLUMNS, TABLE_COLUMN_ORDER } from "./Data/UploadedResourcesTableColumns";
import CustomIcon from "../../Components/CustomIcon";
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { PrimaryFormLoadingActionButton } from "../../Features/Table/GenericDraggableTable/TableStyles";
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { getDataImports, deleteDataImport } from "../../Api/ResourceOnboardingApi";
import DeleteConfirmationModal from "./Components/DeleteConfirmationModal";
import { usePagesContent } from "../../Hooks/usePagesContent";
import AppCache from "../../Helper/Common/AppCache";
import { fetchFiltersDataObject } from "../../Helper/Table/TableHelper";
import { MAX_COLUMN_SELECTION_COUNT } from "../../Helper/DigitalTwin/DataFile";
import { toTitleCase } from "../../Helper/Formatters/TextFormatter";
import { CanCreate } from "../../Components/PermissionGuards";
import { FEATURE_CODES } from "../../Static/FeatureMapping";
import { getImportTypeConfig, IMPORT_TYPE_CONFIG } from "./importTypeAccess";

// Helper function to format bytes to human-readable size
const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';

    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    // Round to 2 decimal places
    const size = (bytes / Math.pow(k, i)).toFixed(2);

    // Remove trailing zeros and decimal point if not needed
    const formattedSize = parseFloat(size).toString();

    return `${formattedSize} ${sizes[i]}`;
};

const TABLE_ID = "UploadedResourcesTable";

const UploadedResourcesTable = ({ onNewUpload }) => {

    const [isLoading, setIsLoading] = useState(false);
    const [pageNumber, setPageNumber] = useState(1);
    const [tableData, setTableData] = useState([]); // Table data from API
    const [pageInfo, setPageInfo] = useState({}); // Page info from API
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [recordToDelete, setRecordToDelete] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);

    // New state for filters, columns, and pagination
    const [selectedColumns, setSelectedColumns] = useState([]);
    const [defaultSelectedColumns, setDefaultSelectedColumns] = useState([]);
    const [columnOrder, setColumnOrder] = useState([]);
    const [availableColumns, setAvailableColumns] = useState([]);
    const [currentTableColumns, setCurrentTableColumns] = useState([]);
    const [availableFilters, setAvailableFilters] = useState({});
    const [selectedFilters, setSelectedFilters] = useState({});
    const [sorting, setSorting] = useState([{ sortBy: "createdAt", order: "desc" }]);

    const pageCount = 10; // Rows per page

    const pageContent = usePagesContent();

    const handleDeleteClick = (record) => {
        setRecordToDelete(record);
        setDeleteDialogOpen(true);
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setRecordToDelete(null);
    };

    const handleDeleteConfirm = async () => {
        if (!recordToDelete || !recordToDelete.importUUID) {
            console.error('No record to delete');
            return;
        }

        setIsDeleting(true);
        try {
            await deleteDataImport(recordToDelete.importUUID);

            // Close dialog
            setDeleteDialogOpen(false);
            setRecordToDelete(null);

            // Refresh table data
            await fetchData();
        } catch (error) {
            console.error('Error deleting data import:', error);
            // You might want to show an error message to the user here
        } finally {
            setIsDeleting(false);
        }
    };

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const filters = {
                ...selectedFilters,
                pageCount: pageCount,
                pageNum: pageNumber,
                ...(sorting?.[0] && {
                    sortBy: sorting[0]['sortBy'],
                    order: sorting[0]['order'],
                }),
            };

            const dataImportResponse = await getDataImports(filters);
            const data = dataImportResponse?.response?.data || [];
            const pageInfo = dataImportResponse?.response?.total || {};

            setPageInfo(pageInfo);
            setTableData(data);
        } catch (error) {
            console.error('Error fetching data imports:', error);
            setTableData([]);
            setPageInfo({});
        } finally {
            setIsLoading(false);
        }
    };

    const customRenderers = useMemo(() => ({
        file_name_custom: (record, config) => {
            // Extract filename from full path (uploadedFileName)
            const fullPath = record.uploadedFileName || "";
            const fileName = fullPath.split('/').pop() || "-";
            const status = record.status?.toLowerCase();
            const showResumeButton = status === 'in_progress';

            return (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CustomIcon name="csv-icon" width="18" height="18" />
                        <Typography variant="fw500s14lh20c2E2E2E" sx={{ lineHeight: '20px' }}>
                            {fileName}
                        </Typography>
                    </Box>

                    {showResumeButton && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1 }}>
                            {/* <Button
                                variant="outlined"
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleResumeClick(record);
                                }}
                                sx={{
                                    borderRadius: '20px',
                                    textTransform: 'none',
                                    fontSize: '14px',
                                    lineHeight: '14px',
                                    padding: '8px 16px',
                                    borderColor: '#BBBBBB',
                                    color: '#2E2E2E',
                                    '&:hover': {
                                        borderColor: '#000000',
                                        backgroundColor: 'inherit'
                                    }
                                }}
                            >
                                Resume
                            </Button> */}
                            <IconButton
                                size="18px"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteClick(record);
                                }}
                                sx={{
                                    color: '#2E2E2E',
                                    borderRadius: '20px',
                                    p: 1,
                                    border: '1px solid #BBBBBB',
                                    '&:hover': {
                                        borderColor: '#000000',
                                        backgroundColor: 'inherit'
                                    }
                                }}
                            >
                                <DeleteOutlineIcon fontSize="18px" />
                            </IconButton>
                        </Box>
                    )}
                </Box>
            );
        },
        status_custom: (record, config) => {
            const status = record.status || "Unknown";

            const getStatusConfig = (status) => {
                const statusLower = status.toLowerCase();
                switch (statusLower) {
                    case 'success':
                        return {
                            icon: <CheckCircleOutlineRoundedIcon sx={{ color: '#008A15', fontSize: '20px' }} />,
                            color: '#008A15',
                            bgColor: '#E8F5E9',
                            displayText: 'Success'
                        };
                    case 'in_progress':
                        return {
                            icon: <CustomIcon name="round-clock-icon" width="20" height="20" stroke="#1565C0" />,
                            color: '#1565C0',
                            bgColor: '#E3F2FD',
                            displayText: 'In Progress'
                        };
                    case 'partial_success':
                        return {
                            icon: <CheckCircleOutlineRoundedIcon sx={{ color: '#DCA101', fontSize: '20px' }} />,
                            color: '#DCA101',
                            bgColor: '#FFF3E0',
                            displayText: 'Partial'
                        };
                    case 'failed':
                        return {
                            icon: <CancelOutlinedIcon sx={{ color: '#C62828', fontSize: '20px' }} />,
                            color: '#C62828',
                            bgColor: '#FFEBEE',
                            displayText: 'Failed'
                        };
                    default:
                        return {
                            icon: null,
                            color: '#616161',
                            bgColor: '#F5F5F5',
                            displayText: status
                        };
                }
            };

            const statusConfig = getStatusConfig(status);

            return (
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    width: 'fit-content'
                }}>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: statusConfig.color
                    }}>
                        {statusConfig.icon}
                    </Box>
                    <Typography
                        variant="fw500s14lh20c2E2E2E"
                        sx={{
                            fontSize: '14px',
                            lineHeight: '20px'
                        }}
                    >
                        {statusConfig.displayText}
                    </Typography>
                </Box>
            );
        },
        upload_type_custom: (record, config) => {
            const type = record.dataImportType || "";
            // Prefer the backend displayName; fall back to the local type->label map.
            const label = record.dataImportTypeDisplayName || getImportTypeConfig(type).label;

            return (
                <Typography variant="fw500s14lh20c2E2E2E" sx={{ lineHeight: '20px' }}>
                    {label}
                </Typography>
            );
        },
        file_size_custom: (record, config) => {
            const bytes = record.uploadedFileSize || 0;
            const formattedSize = formatFileSize(bytes);

            return (
                <Typography variant="fw500s14lh20c2E2E2E" sx={{ lineHeight: '20px' }}>
                    {formattedSize}
                </Typography>
            );
        },
    }), []);

    // Setup table columns from pageContent
    const setupTableColumns = () => {
        const tableInfo = pageContent?.metaInfo?.table?.resourceOnboardingTable || {};
        const tableColumns = tableInfo?.columnsConfig || TABLE_COLUMNS;
        const tableColumnOrder = tableInfo?.columnOrdering || TABLE_COLUMN_ORDER;
        const availableColumns = getColumnsConfig({
            tableColumns: tableColumns,
            tableColumnOrder: tableColumnOrder,
            customRenderers,
        });

        setAvailableColumns(availableColumns);

        const initalSelectedColumns = Object.keys(tableColumns) || [];

        // sort by fixed key and then pick the first MAX_COLUMN_SELECTION_COUNT columns
        const defaultColumns = initalSelectedColumns
            .sort((a, b) => {
                if (a.fixed && !b.fixed) return -1;
                if (!a.fixed && b.fixed) return 1;
                return 0;
            })
            .slice(0, MAX_COLUMN_SELECTION_COUNT);

        setSelectedColumns(defaultColumns);
        setDefaultSelectedColumns(defaultColumns);

        const currentTableColumns = availableColumns.filter((column) =>
            initalSelectedColumns.includes(column.key)
        );
        setCurrentTableColumns(currentTableColumns);
        setColumnOrder(currentTableColumns.map((column) => column.headerLabel));
    };

    // Setup table filters from pageContent
    const setupTableFilters = async () => {
        const tableFilters =
            pageContent?.metaInfo?.filterConfigurations?.resourceOnboardingTable?.filters ||
            [];
        const filtersObj = await fetchFiltersDataObject(
            tableFilters,
            availableFilters
        );

        // Convert all filter values to title case
        Object.keys(filtersObj).forEach(filterKey => {
            if (filtersObj[filterKey]?.values) {
                filtersObj[filterKey].values = filtersObj[filterKey].values.map(item => {
                    // Import type codes (e.g. INVENTORY_NEW) show their display label
                    if (item.value === item.id && IMPORT_TYPE_CONFIG[item.id]) {
                        return { ...item, value: IMPORT_TYPE_CONFIG[item.id].label };
                    }
                    // Only convert if value is same as id (all uppercase pattern)
                    if (item.value === item.id && item.id === item.id.toUpperCase()) {
                        return { ...item, value: toTitleCase(item.id) };
                    }
                    return item;
                });
            }
        });

        setAvailableFilters(filtersObj);
    };

    // Apply selected columns
    const applySelectedColumns = (newSelectedColumns) => {
        AppCache.setItem(TABLE_ID, { selectedColumns: newSelectedColumns });
        setSelectedColumns(newSelectedColumns);
    };

    // Apply selected filters
    const applySelectedFilters = (filters) => {
        setSelectedFilters(filters);
        setPageNumber(1); // Reset to first page when filters change
    };

    // Handle column order change
    const onColumnOrderChange = (tableColumns) => {
        const updatedColumnOrder = tableColumns.map((value) => value.headerLabel);
        setColumnOrder(updatedColumnOrder);
    };

    // Handle page change
    const onPageChange = (pageNo) => {
        setPageNumber(pageNo);
    };

    // Apply sort
    const applySort = (key, order) => {
        const sortDetails = { sortBy: key, order: order };
        setSorting([sortDetails]);
        setPageNumber(1); // Reset to first page when sorting
    };

    // Setup columns and filters when pageContent changes
    useEffect(() => {
        setupTableColumns();
        setupTableFilters();
    }, [pageContent]);

    // Update current table columns when selected columns change
    useEffect(() => {
        const currentTableColumns = availableColumns.filter((column) =>
            selectedColumns.includes(column.key)
        );
        setCurrentTableColumns(currentTableColumns);
    }, [selectedColumns, availableColumns]);

    // Fetch data when filters or sorting change (reset to page 1)
    useEffect(() => {
        pageNumber === 1 ? fetchData() : setPageNumber(1);
    }, [selectedFilters, sorting]);

    // Fetch data when page number changes
    useEffect(() => {
        fetchData();
    }, [pageNumber]);

    return (
        <>
            {/* Delete Confirmation Dialog */}
            <DeleteConfirmationModal
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                onConfirm={handleDeleteConfirm}
                isDeleting={isDeleting}
            />

            <Box sx={{ mb: 3, width: "calc(100vw - 102px)" }}>
                <GenericDraggableTableView
                    title="Upload History"
                    tableID={TABLE_ID}
                    showPrimaryButton={false}
                    disableColumnSettings={true}
                    applySearch={(selectedDevices, selectedSearchColumn) => {}}
                    disableSearch={true}
                    disableDownloads={true}
                    filters={availableFilters}
                    selectedFilters={selectedFilters}
                    applyFilters={applySelectedFilters}
                    initialColumns={availableColumns}
                    tableColumns={currentTableColumns}
                    selectedColumns={selectedColumns}
                    applySelectedColumns={applySelectedColumns}
                    tableData={tableData}
                    onColumnOrderChange={onColumnOrderChange}
                    columnOrder={columnOrder}
                    isLoading={isLoading}
                    pageNumber={pageNumber}
                    rowsPerPage={pageCount}
                    count={pageInfo?.total || 0}
                    total={pageInfo?.total || 0}
                    applySort={applySort}
                    onPageChange={onPageChange}
                    emptyMessage="No uploaded files found"
                    customRightActions={true}
                    customRightActionsView={(
                        <CanCreate feature={FEATURE_CODES.RESOURCE_ONBOARDING}>
                            <Box sx={{ ml: 1 }}>
                                <PrimaryFormLoadingActionButton onClick={onNewUpload} startIcon={<AddIcon />}>
                                    New Upload
                                </PrimaryFormLoadingActionButton>
                            </Box>
                        </CanCreate>
                    )}
                    maxColumnsAllowed={MAX_COLUMN_SELECTION_COUNT}
                    defaultSelectedColumns={defaultSelectedColumns}
                />
            </Box>

        </>
    )
}

export default UploadedResourcesTable;
